import React, { useEffect, useReducer, useRef } from 'react';
import { askReducer, estadoInicial, podeEnviar, AskState } from './askFieldState';
import './AskField.css';

/**
 * A casca compartilhada dos campos de pergunta.
 *
 * Três telas fazem a mesma coisa por fora — caixa de texto, botão de seta,
 * spinner, dicas clicáveis, erro, limpar — e faziam com dois vocabulários CSS
 * diferentes (`tl-ask-*` com 17 classes, `ax-*` com 25). O terceiro campo seria
 * o terceiro vocabulário, e o próximo ajuste de contraste ou de mobile seria
 * feito em duas das três cópias. Foi assim que o botão excluir de Métodos de
 * Pagamento ficou a 1,34:1 sem ninguém ver.
 *
 * A divisão: a CASCA é idêntica nos três, o RESULTADO é diferente em cada um.
 * Por isso o resultado entra como função-filha, e não como mais uma prop de
 * configuração — nenhuma das três telas precisa saber das outras duas.
 *
 * O estado vive em `askFieldState.ts`, puro e testado.
 */

interface Props<R> {
  placeholder: string;
  /** Frases clicáveis, mostradas só enquanto não há pergunta nem resposta. */
  exemplos?: string[];
  /** Faz a pergunta. Lançar aqui vira a mensagem de erro. */
  onAsk: (texto: string) => Promise<R>;
  /**
   * Renderiza a resposta DENTRO do campo. Omitido no modo conversa, onde a
   * resposta vira mensagem numa lista que o pai controla.
   */
  children?: (resultado: R, limpar: () => void) => React.ReactNode;
  /** Modo conversa: o pai recebe o resultado e o campo se esvazia para a próxima. */
  onResult?: (resultado: R) => void;
  /** Conteúdo sempre visível acima do campo. É onde o número em repouso mora. */
  header?: React.ReactNode;
  ariaLabel?: string;
  /** Escreve a pergunta no campo, sem enviar. O `em` faz o mesmo texto valer de novo. */
  textoInicial?: { texto: string; em: number } | null;
  /**
   * Caixa que cresce com o texto, em vez de linha única. Na bolha de pergunta
   * do celular a pergunta passava da largura e sumia pela esquerda: ela não
   * via o que tinha escrito.
   */
  multilinha?: boolean;
}

function AskField<R>({ placeholder, exemplos = [], onAsk, children, onResult, header, ariaLabel, textoInicial, multilinha }: Props<R>) {
  const [estado, dispatch] = useReducer(
    askReducer as React.Reducer<AskState<R>, any>,
    undefined,
    estadoInicial<R>,
  );
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  // A altura acompanha o conteúdo: zera e mede de novo a cada mudança, para
  // encolher também quando ela apaga. O teto mora no CSS (`max-height`).
  useEffect(() => {
    const el = inputRef.current;
    if (!multilinha || !el) return;
    el.style.height = 'auto';
    // `scrollHeight` não conta a borda; sem somá-la a caixa fica 3px curta e
    // aparece uma barra de rolagem à toa.
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [multilinha, estado.texto]);

  useEffect(() => {
    if (!textoInicial) return;
    dispatch({ type: 'digitar', texto: textoInicial.texto });
    inputRef.current?.focus();
  }, [textoInicial]);

  const perguntar = async () => {
    if (!podeEnviar(estado)) return;
    const texto = estado.texto.trim();
    dispatch({ type: 'enviar' });
    try {
      const r = await onAsk(texto);
      if (onResult) {
        // Conversa: a resposta sai daqui e vira mensagem lá em cima. O campo
        // volta ao zero para a próxima pergunta, que é o que um compositor faz.
        onResult(r);
        dispatch({ type: 'limpar' });
        inputRef.current?.focus();
      } else {
        dispatch({ type: 'sucesso', resultado: r });
      }
    } catch (e: any) {
      // Duas formas chegam aqui: erro do axios (mensagem do servidor aninhada)
      // e Error lançado pelo próprio `onAsk` quando a resposta veio 200 mas
      // sem conteúdo utilizável.
      dispatch({
        type: 'falha',
        erro: e?.response?.data?.error ?? e?.message ?? 'Não consegui responder agora.',
      });
    }
  };

  const limpar = () => { dispatch({ type: 'limpar' }); inputRef.current?.focus(); };

  return (
    <div className="af">
      {header}

      <div className="af-row">
        {React.createElement(multilinha ? 'textarea' : 'input', {
          ref: inputRef,
          className: `af-input${multilinha ? ' af-input-multi' : ''}`,
          placeholder,
          'aria-label': ariaLabel ?? placeholder,
          value: estado.texto,
          rows: multilinha ? 1 : undefined,
          // No teclado do celular a tecla de Enter vira "enviar", não quebra de
          // linha: a caixa cresce sozinha, ninguém precisa de Enter para isso.
          enterKeyHint: multilinha ? 'send' : undefined,
          onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            dispatch({ type: 'digitar', texto: e.target.value }),
          // A saída do aviso: voltar ao campo para perguntar de novo. Era um
          // timer de 10s, e ele apagava a explicação antes de ela ler.
          onFocus: () => dispatch({ type: 'focar' }),
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); perguntar(); }
            if (e.key === 'Escape') limpar();
          },
          disabled: estado.carregando,
        })}
        <button
          className="af-go"
          onClick={perguntar}
          disabled={!podeEnviar(estado)}
          aria-label="Perguntar"
          title="Perguntar (Enter)"
        >
          {estado.carregando ? <span className="af-spin" /> : '→'}
        </button>
      </div>

      {exemplos.length > 0 && !estado.resultado && !estado.erro && !estado.texto && (
        <div className="af-hints">
          {exemplos.map(ex => (
            <button
              key={ex}
              className="af-hint"
              onClick={() => { dispatch({ type: 'digitar', texto: ex }); inputRef.current?.focus(); }}
            >{ex}</button>
          ))}
        </div>
      )}

      {/*
        O erro precisa de saída própria.

        Todo outro estado deste campo tem uma: `resultado` traz o botão
        "limpar" junto, e `texto` sai com Escape. O erro não tinha nenhuma —
        ele aparecia e ficava até ela digitar de novo, sem X e sem sumir. Foi
        relatado assim: "esse erro que não some mais".

        Agora ele sai por GESTO: clicar no campo, digitar, ou o X. Nunca por
        relógio — um timer apagava a explicação enquanto ela ainda estava
        lendo. Os três fecham SÓ o aviso e preservam o texto digitado.

        Fica INLINE e não vira tooltip de verdade: tooltip depende de hover, e
        no celular não existe hover. Um erro que ela não consegue reler é pior
        que um que fica tempo demais.
      */}
      {estado.erro && (
        <div className="af-erro" role="alert">
          <span>{estado.erro}</span>
          <button
            className="af-erro-x"
            onClick={() => { dispatch({ type: 'fecharErro' }); inputRef.current?.focus(); }}
            aria-label="Fechar aviso"
            title="Fechar"
          >×</button>
        </div>
      )}

      {estado.resultado != null && children?.(estado.resultado, limpar)}
    </div>
  );
}

export default AskField;
